import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import * as VariantActions from '../../../../actions/variants'
import { useAppDispatch } from '../../../../actions/types'
import { colors, Routes } from '../../../../constants'
import * as PromosActions from '../../../../actions/promos'
import {
    Button,
    Column,
    Confirmation,
    Form,
    Headings,
    Input,
    Margin,
    Padding,
} from '../../../../components'
import { Label } from '../../../../components/Form'
import { Sizes } from '../../../../components/Margin'
import type { Variant } from '../../../../types'
import type { VariantFormData } from '../types'
import useVariantParams from '../useVariantParams'
import * as Validation from '../validation'
import ErrorMessages from './ErrorMessages'
import useVariantFormData from './useVariantFormData'
import { FullWidthInput, FullWidthLabel, NameSection } from '../../Promos/PromoForm/PromoForm.styles'
import MultiDateInput from '../../Promos/PromoForm/MultiDateInput'
import moment from 'moment'
import useDisplayBrands from '../../Promos/PromoForm/useDisplayBrands'

const parseSkuInput = (rawValue: string, { allowHyphen = false } = {}) => (
    rawValue
        .replace(/([A-Za-z0-9_])\s$/, '$1,')
        .split(',')
        .map(sku => sku.replace(allowHyphen ? /[^A-Za-z0-9_-]/g : /[^A-Za-z0-9_]/g, ''))
)

export default function VariantForm({ variant }: { variant?: Variant }) {
    const dispatch = useAppDispatch()
    const history = useHistory()
    const { t } = useTranslation()
    const { estate } = useVariantParams()
    const displayBrands = useDisplayBrands()
    const {
        areUnsavedChanges,
        formErrors,
        onSubmit,
        variantData,
        saveProgress,
        setVariantData,
        setUnsavedChanges,
        submitError,
    } = useVariantFormData(variant)

    const {
        id,
        class: variantClass,
        priority,
        start_date,
        end_date,
        skus,
        exceptions,
    } = variantData

    React.useEffect(() => {
        dispatch(PromosActions.fetchBrands())
    }, [dispatch])

    const buildOnChange = (attr: Exclude<keyof VariantFormData, 'start_date' | 'end_date' | 'exceptions'>) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            setUnsavedChanges(true)

            let value: string | string[] | number | null = event.target.value

            if (attr === 'priority') {
                value = parseInt(value as string, 10)
            }

            if (attr === 'brand') {
                value = value === '' ? null : value as string
            }

            if (attr === 'skus') {
                value = parseSkuInput(value as string)
            }

            setVariantData(prevData => {
                return {
                    ...prevData,
                    [attr]: value,
                }
            })
        }
    }

    const buildOnChangeExceptionArray = (field: keyof VariantFormData['exceptions']) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            setUnsavedChanges(true)
            const value = parseSkuInput(event.target.value as string, { allowHyphen: true })

            setVariantData(prevData => ({
                ...prevData,
                exceptions: {
                    ...prevData.exceptions,
                    [field]: value,
                },
            }))
        }
    }

    const onChangeExceptionSkus = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUnsavedChanges(true)

        setVariantData(prevData => ({
            ...prevData,
            exceptions: {
                ...prevData.exceptions,
                skus: parseSkuInput(event.target.value as string),
            },
        }))
    }

    const buildOnChangePrice = (field: keyof VariantFormData['exceptions']['price']) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            setUnsavedChanges(true)
            const raw = event.target.value
            const value = raw === '' ? null : parseFloat(raw)

            setVariantData(prevData => ({
                ...prevData,
                exceptions: {
                    ...prevData.exceptions,
                    price: {
                        ...prevData.exceptions.price,
                        [field]: value,
                    },
                },
            }))
        }
    }

    const onChangeDates = (attr: 'end_date' | 'start_date') => {
        return (value: number) => {
            setUnsavedChanges(true)
            setVariantData(prevData => {
                return {
                    ...prevData,
                    [attr]: new Date(value),
                }
            })
        }
    }

    const onDeleteVariant = () => {
        if (!id) {
            return
        }

        const fn = async () => {
            await dispatch(VariantActions.deleteVariant(id))

            history.replace(
                Routes.buildRoute(Routes.SELF_SERVE_VARIANTS, { estate })
            )
        }

        fn()
    }

    const isNew = !variant

    const thereAreErrors =
        (validation: Validation.VariantValidation) => Object.keys(validation).length > 0

    const firstError = thereAreErrors(formErrors)
        ? formErrors[Object.keys(formErrors)[0] as keyof Validation.VariantValidation]
        : undefined

    const SkusLabel = skus.length === 0
        ? t('variant-skus')
        : `${t('variant-skus')} (${skus.length})`

    const RegionsLabel = exceptions.regions.length === 0
        ? t('variant-exception-regions')
        : `${t('variant-exception-regions')} (${exceptions.regions.length})`

    const StoresLabel = exceptions.stores.length === 0
        ? t('variant-exception-stores')
        : `${t('variant-exception-stores')} (${exceptions.stores.length})`

    const UidsLabel = exceptions.uids.length === 0
        ? t('variant-exception-uids')
        : `${t('variant-exception-uids')} (${exceptions.uids.length})`

    const ExcepSkusLabel = exceptions.skus.length === 0
        ? t('variant-exception-skus')
        : `${t('variant-exception-skus')} (${exceptions.skus.length})`

    const DatesLabel = exceptions.dates.length === 0
        ? t('variant-exception-dates')
        : `${t('variant-exception-dates')} (${exceptions.dates.length})`

    return (
        <Padding>
            <Form>
                <NameSection top bottom>
                    <Column width="40%">
                        <Headings.H2>
                            {isNew ? t('variant-create') : t('variant-title')}
                        </Headings.H2>
                    </Column>
                    <Column width="60%" right>
                        <ErrorMessages
                            saveProgress={saveProgress}
                            areUnsavedChanges={areUnsavedChanges}
                            errorMessage={firstError ?? submitError?.message}
                        />
                        <Button
                            theme="forestGreen"
                            onClick={onSubmit}
                            disabled={!areUnsavedChanges}
                        >
                            {t('variant-save')}
                        </Button>

                        <Confirmation
                            cancel={t('cancel')}
                            confirmation={t('variant-remove-confirm-body')}
                            header={t('variant-remove-confirm-header')}
                            onCancel={() => null}
                            onConfirm={onDeleteVariant}
                            ok={t('variant-remove-confirm-button')}
                            theme="danger"
                        >
                            <Button
                                theme="danger"
                                disabled={isNew}
                            >
                                {t('variant-delete')}
                            </Button>
                        </Confirmation>
                    </Column>
                </NameSection>
                <Padding color={colors.white}>
                    <Column width="60%">
                        {!isNew && (
                            <Label id="variant-id" label="ID" error={null}>
                                <Input
                                    id="variant-id"
                                    name="variant-id"
                                    type="text"
                                    size="sm"
                                    value={String(id)}
                                    disabled
                                />
                            </Label>
                        )}
                        <Label id="variant-class" label={t('variant-class')} error={null}>
                            <Input
                                id="variant-class"
                                name="variant-class"
                                type="select"
                                values={[
                                    { name: 'Sale', value: 'sale-sku' },
                                    { name: 'St. Patricks', value: 'st-patricks' },
                                    { name: 'Christmas', value: 'xmas' },
                                    { name: 'Black Friday', value: 'black-friday' },
                                ]}
                                error={formErrors.class && t(formErrors.class)}
                                onChange={buildOnChange('class')}
                                size="sm"
                                value={variantClass}
                            />
                        </Label>
                        <Label id="variant-priority" label={t('variant-priority')} error={null}>
                            <Input
                                id="variant-priority"
                                name="variant-priority"
                                type="number"
                                error={formErrors.priority && t(formErrors.priority)}
                                onChange={buildOnChange('priority')}
                                size="sm"
                                value={priority}
                            />
                        </Label>
                        <Label id="variant-brand" label={t('promo-brand')} error={null}>
                            <Input
                                id="variant-brand"
                                name="variant-brand"
                                type="select"
                                values={displayBrands.map(brand => ({
                                    ...brand,
                                    value: brand.value ?? '',
                                }))}
                                onChange={buildOnChange('brand')}
                                size="sm"
                                value={variantData.brand ?? ''}
                            />
                        </Label>
                        <Label id="variant-start-date" label={t('variant-start-date')} error={null}>
                            <Input
                                id="variant-start-date"
                                name="variant-start-date"
                                type="date"
                                error={formErrors.start_date && t(formErrors.start_date)}
                                onChange={onChangeDates('start_date')}
                                size="sm"
                                value={start_date instanceof Date ? start_date.getTime() : start_date}
                            />
                        </Label>
                        <Label id="variant-end-date" label={t('variant-end-date')} error={null}>
                            <Input
                                id="variant-end-date"
                                name="variant-end-date"
                                type="date"
                                error={formErrors.end_date && t(formErrors.end_date)}
                                onChange={onChangeDates('end_date')}
                                size="sm"
                                value={end_date instanceof Date ? end_date.getTime() : end_date}
                            />
                        </Label>
                            <FullWidthLabel id="variant-skus" label={SkusLabel} error={null}>
                                <Input
                                    id="variant-skus"
                                    name="variant-skus"
                                    type="textarea"
                                    error={null}
                                    onChange={buildOnChange('skus')}
                                    size="lg"
                                    value={skus.join(', ')}
                                    style={{ height: '85px', width: '80%' }}
                                    placeholder="654961, 5216854, 843159..."
                                />
                            </FullWidthLabel>
                    </Column>
                    <Column width="40%" left>
                        <Headings.H3>{t('variant-exclusions')}</Headings.H3>
                        <FullWidthLabel id="variant-exception-regions" label={RegionsLabel} error={null}>
                            <Input
                                id="variant-exception-regions"
                                name="variant-exception-regions"
                                type="textarea"
                                onChange={buildOnChangeExceptionArray('regions')}
                                size="lg"
                                value={exceptions.regions.join(',')}
                            />
                        </FullWidthLabel>
                        <FullWidthLabel id="variant-exception-stores" label={StoresLabel} error={null}>
                            <Input
                                id="variant-exception-stores"
                                name="variant-exception-stores"
                                type="textarea"
                                onChange={buildOnChangeExceptionArray('stores')}
                                size="lg"
                                value={exceptions.stores.join(',')}
                            />
                        </FullWidthLabel>
                        <FullWidthLabel id="variant-exception-uids" label={UidsLabel} error={null}>
                            <Input
                                id="variant-exception-uids"
                                name="variant-exception-uids"
                                type="textarea"
                                onChange={buildOnChangeExceptionArray('uids')}
                                size="lg"
                                value={exceptions.uids.join(',')}
                            />
                        </FullWidthLabel>
                        <FullWidthLabel id="variant-exception-skus" label={ExcepSkusLabel} error={null}>
                            <Input
                                id="variant-exception-skus"
                                name="variant-exception-skus"
                                type="textarea"
                                error={null}
                                onChange={onChangeExceptionSkus}
                                size="lg"
                                value={exceptions.skus.join(', ')}
                                style={{ height: '85px', width: '100%' }}
                                placeholder={t('promo-exception-skus-placeholder')}
                            />
                        </FullWidthLabel>
                        <FullWidthLabel id="variant-exception-dates" label={DatesLabel} error={null}>
                            <MultiDateInput
                                onDeleteDate={() => {
                                    setUnsavedChanges(true)
                                    setVariantData(prev => ({
                                        ...prev,
                                        exceptions: {
                                            ...prev.exceptions,
                                            dates: prev.exceptions.dates.slice(0, -1),
                                        },
                                    }))
                                }}
                                onEnterDate={(newDate: Date) => {
                                    setUnsavedChanges(true)
                                    setVariantData(prev => {
                                        const alreadyInserted = prev.exceptions.dates.some(
                                            date => moment(date).format('DD/MM/YYYY') === moment(newDate).format('DD/MM/YYYY'),
                                        )

                                        if (alreadyInserted) {
                                            return prev
                                        }

                                        const updatedDates = [
                                            ...prev.exceptions.dates,
                                            newDate,
                                        ].sort((a, b) => a.getTime() - b.getTime())

                                        return {
                                            ...prev,
                                            exceptions: {
                                                ...prev.exceptions,
                                                dates: updatedDates,
                                            },
                                        }
                                    })
                                }}
                                values={exceptions.dates}
                            />
                        </FullWidthLabel>
                        <FullWidthLabel id="variant-exception-price" label={t('variant-exception-price')} error={null}>
                            <FullWidthInput
                                id="variant-exception-price-lt"
                                name="variant-exception-price-lt"
                                type="text"
                                onChange={buildOnChangePrice('less_than')}
                                size="lg"
                                value={exceptions.price.less_than ?? ''}
                                placeholder={t('variant-exception-price-lt')}
                            />
                            <Margin top size={Sizes.xs}>
                                <FullWidthInput
                                    id="variant-exception-price-gt"
                                    name="variant-exception-price-gt"
                                    type="text"
                                    onChange={buildOnChangePrice('greater_than')}
                                    size="lg"
                                    value={exceptions.price.greater_than ?? ''}
                                    placeholder={t('variant-exception-price-gt')}
                                />
                            </Margin>
                        </FullWidthLabel>
                    </Column>
                </Padding>
            </Form>
        </Padding>
    )
}
